import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { v4 as uuidv4 } from "uuid";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import {
  CardImageStyled,
  CardsContainerStyled,
  CardWrapper,
} from "./CardSearch.styled";
import { LabelStyled, StyledInput } from "../../common/common.style";
import { CardMarketCompatible } from "../../common/CardMarketCompatible/CardMarketCompatible";
import { Box, FlexBox } from "../../primitives";

import { selectGame } from "../../../store/slices/global";
import { CardSelected, MtgCardType, StatsDataType } from "../../../type";
import {
  useSearchFunction,
  useSearchResults,
  useSearchReady,
  useClearSearchResults,
  useCardLimit,
} from "../../../context/CardsContext";
import { CheckBox } from "../../common/CheckBox/CheckBox";

interface CardSearchProps {
  defaultStatsValue: StatsDataType;
  setCardsSelected: (arg1: CardSelected) => void;
  cardSelected?: CardSelected | null;
}

const sanitize = (str: string): string =>
  str
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();

export const CardSearch = ({
  setCardsSelected,
  defaultStatsValue,
  cardSelected,
}: CardSearchProps) => {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const { cardLimit, setCardLimit } = useCardLimit();

  const game = useSelector(selectGame);
  const search = useSearchFunction();
  const results = useSearchResults();
  const isSearchReady = useSearchReady();
  const clearSearchResults = useClearSearchResults();

  const inputRef = useRef<HTMLInputElement>(null);
  const resultRefs = useRef<(HTMLDivElement | null)[]>([]);
  const resultsContainerRef = useRef<HTMLDivElement | null>(null);

  const debouncedSearch = useMemo(
    () =>
      debounce((raw: string) => {
        const cleaned = sanitize(raw);
        if (cleaned.length < 4 || !isSearchReady) {
          return;
        }
        search(cleaned);
      }, 200),
    [search, isSearchReady]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedIndex !== 1) setSelectedIndex(1);
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    if (!cardSelected) inputRef.current?.focus();
  }, [cardSelected]);

  useEffect(() => {
    const ref = resultRefs.current[selectedIndex - 1];
    const container = resultsContainerRef.current;
    if (ref && container) {
      const itemRect = ref.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (
        itemRect.top < containerRect.top ||
        itemRect.bottom > containerRect.bottom
      ) {
        ref.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (results.length > 0) {
      setSelectedIndex(1);
    } else {
      setSelectedIndex(-1);
    }
  }, [results]);

  const handleSelect = useCallback(
    (card: MtgCardType) => {
      setCardsSelected({
        uid: uuidv4(),
        id: card.id,
        name: card.name,
        originalPrice: defaultStatsValue.price,
        game_id: game.id,
        date: dayjs().toISOString(),
        ...defaultStatsValue,
      });
    },
    [setCardsSelected, defaultStatsValue, game]
  );

  const handleCardClick = useCallback(
    (card: MtgCardType) => {
      inputRef.current?.blur();
      setQuery("");
      handleSelect(card);
      setSelectedIndex(-1);
      clearSearchResults();
    },
    [handleSelect, clearSearchResults]
  );

  useEffect(() => {
    const handleKeys = (e: KeyboardEvent) => {
      if (cardSelected) return;
      if (!results.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        if (selectedIndex > 1) {
          e.preventDefault();
          setSelectedIndex((prev) => prev - 1);
        }
      } else if (e.key === "Enter" && selectedIndex >= 1) {
        const card = results[selectedIndex - 1];
        if (card) handleCardClick(card);
      } else if (e.key === "Escape") {
        setQuery("");
      }
    };
    window.addEventListener("keydown", handleKeys);
    return () => window.removeEventListener("keydown", handleKeys);
  }, [results, selectedIndex, handleCardClick, cardSelected]);

  return (
    <Box css={{ position: "relative" }}>
      <Box css={{ padding: 20, maxWidth: 400 }}>
        <StyledInput
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="Search card name or edition+number..."
          ref={inputRef}
        />
        <FlexBox css={{ alignItems: "center", gap: 10, marginTop: 20 }}>
          <LabelStyled>Extend research</LabelStyled>
          <CheckBox
            checked={cardLimit === 100}
            setChecked={(checked: boolean) => {
              setCardLimit(checked ? 100 : 10);
            }}
          />
        </FlexBox>
        {results.length > 0 && query && (
          <CardsContainerStyled ref={resultsContainerRef}>
            {results.slice(0, 100).map((card, index) => (
              <CardWrapper
                key={card.id}
                css={{
                  background:
                    selectedIndex === index + 1 ? "$lighterGreen" : "white",
                }}
                ref={(el) => {
                  resultRefs.current[index] = el;
                }}
                onClick={() => handleCardClick(card)}
              >
                <CardMarketCompatible
                  cardmarketProductId={card.card_market_ids[0]}
                />
                <Box css={{ textAlign: "center" }}>
                  <Box
                    css={{
                      fontSize: 14,
                      width: 200,
                      fontWeight: "bold",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {card.name}
                  </Box>
                  <Box
                    css={{
                      fontSize: 8,
                      color: selectedIndex === index + 1 ? "white" : "#555",
                      textTransform: "uppercase",
                      width: 200,
                      fontWeight: "bold",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginTop: 8,
                    }}
                  >
                    {`${card.fixed_properties.collector_number} ${card.editionCode} - ${card.editionName}`}
                  </Box>
                  <CardImageStyled
                    src={card.image_url}
                    alt={card.name}
                    loading="lazy"
                  />
                </Box>
              </CardWrapper>
            ))}
          </CardsContainerStyled>
        )}
      </Box>
    </Box>
  );
};

export default CardSearch;
